/** FX rate table, conversion, realised FX and rate-source parsers (Frankfurter JSON, RBA F11.1 CSV). Pure. */
import { z } from 'zod';
import { KeelError } from '../errors.js';
import { Decimal, minorUnits } from '../money.js';
import { isISODate } from '../dates.js';
import { parseCsv } from '../importers/csv.js';
import { parseDate } from '../importers/dates.js';
const pairKey = (base, quote) => `${base.toUpperCase()}/${quote.toUpperCase()}`;
const toMinor = (major, ccy) => major.mul(10 ** minorUnits(ccy)).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/**
 * In-memory FX table. Convention (same as `FxRate`): 1 `base` = `rate` × `quote`
 * (AUD/USD 0.65 means A$1 buys US$0.65). Lookups take the latest rate dated on or before the requested date;
 * a direct or inverse pair is used when present (whichever is more recent), else a cross via the first pivot that works.
 */
export class FxTable {
    rates;
    pivots;
    series = new Map();
    constructor(rates, opts = {}) {
        this.rates = rates;
        this.pivots = (opts.pivots ?? ['AUD', 'USD', 'EUR']).map((c) => c.toUpperCase());
        for (const r of rates) {
            const k = pairKey(r.base, r.quote);
            const list = this.series.get(k);
            if (list)
                list.push(r);
            else
                this.series.set(k, [r]);
        }
        for (const list of this.series.values())
            list.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    }
    /** Latest rate in `pair` dated on or before `date` (binary search). */
    prior(base, quote, date) {
        const list = this.series.get(pairKey(base, quote));
        if (!list)
            return null;
        let lo = 0;
        let hi = list.length - 1;
        let found = null;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            const r = list[mid];
            if (r.date <= date) {
                found = r;
                lo = mid + 1;
            }
            else
                hi = mid - 1;
        }
        return found;
    }
    simple(base, quote, date) {
        const direct = this.prior(base, quote, date);
        const inverse = this.prior(quote, base, date);
        if (direct && (!inverse || direct.date >= inverse.date))
            return direct.rate;
        return inverse ? 1 / inverse.rate : null;
    }
    /** Units of `quote` per 1 `base` on `date`, or null when no direct, inverse or pivot-cross rate exists. */
    rateOn(base, quote, date) {
        const b = base.toUpperCase();
        const q = quote.toUpperCase();
        if (b === q)
            return 1;
        const s = this.simple(b, q, date);
        if (s !== null)
            return s;
        for (const p of this.pivots) {
            if (p === b || p === q)
                continue;
            const leg1 = this.simple(b, p, date);
            const leg2 = leg1 === null ? null : this.simple(p, q, date);
            if (leg1 !== null && leg2 !== null)
                return leg1 * leg2;
        }
        return null;
    }
    /** Convert minor units of `from` into minor units of `to` (half-even). Throws NOT_FOUND without a rate. */
    convert(cents, from, to, date) {
        if (from.toUpperCase() === to.toUpperCase())
            return cents;
        const rate = this.rateOn(from, to, date);
        if (rate === null)
            throw new KeelError('NOT_FOUND', `No FX rate ${from}/${to} on or before ${date}`, { from, to, date });
        return toMinor(new Decimal(cents).div(10 ** minorUnits(from)).mul(rate), to);
    }
}
/** Most recent rate per base/quote pair. */
export const latestRates = (table) => {
    const rates = table instanceof FxTable ? table.rates : table;
    const latest = new Map();
    for (const r of rates) {
        const k = pairKey(r.base, r.quote);
        const cur = latest.get(k);
        if (!cur || r.date > cur.date)
            latest.set(k, r);
    }
    return [...latest.values()];
};
/**
 * Realised FX gain/loss in base-currency minor units on settling `originalCents` of `originalCcy`.
 * Rates are quoted as units of `originalCcy` per 1 base unit (an FxRate with base = `baseCcy`, e.g. AUD/USD 0.70).
 * Result = base value at settlement − base value at booking, each rounded half-even: positive is a gain on a
 * receivable (positive amount) and automatically a loss for a payable (negative amount) when rates move the same way.
 */
export const realisedFxGain = (originalCents, originalCcy, bookedRate, settledRate, baseCcy = 'AUD') => {
    if (!(bookedRate > 0) || !(settledRate > 0))
        throw new KeelError('VALIDATION', 'FX rates must be positive', { bookedRate, settledRate });
    const major = new Decimal(originalCents).div(10 ** minorUnits(originalCcy));
    return toMinor(major.div(settledRate), baseCcy) - toMinor(major.div(bookedRate), baseCcy);
};
const mkRate = (base, quote, date, rate, source) => ({ base: base.toUpperCase(), quote: quote.toUpperCase(), date, rate, source });
const FrankRates = z.record(z.string(), z.number().positive());
const FrankSingle = z.object({ amount: z.number().positive(), base: z.string().length(3), date: z.string().refine(isISODate), rates: FrankRates });
const FrankSeries = z.object({ amount: z.number().positive(), base: z.string().length(3), rates: z.record(z.string().refine(isISODate), FrankRates) });
/** Parse a Frankfurter response: single date `{amount,base,date,rates}` or series `{amount,base,start_date,end_date,rates:{date:{CCY:n}}}`. */
export const fromFrankfurterJson = (json) => {
    const single = FrankSingle.safeParse(json);
    if (single.success) {
        const { base, date, amount, rates } = single.data;
        return Object.entries(rates).map(([q, r]) => mkRate(base, q, date, r / amount, 'frankfurter'));
    }
    const series = FrankSeries.safeParse(json);
    if (!series.success)
        throw new KeelError('PROVIDER', 'Unrecognised Frankfurter response', { issues: series.error.issues });
    const { base, amount, rates } = series.data;
    return Object.keys(rates).sort().flatMap((d) => Object.entries(rates[d]).map(([q, r]) => mkRate(base, q, d, r / amount, 'frankfurter')));
};
/**
 * Parse an RBA F11.1 "Exchange Rates" CSV (header block: Title, Description, Frequency, Type, Units, Source,
 * Publication date, Series ID; then `dd-MMM-yyyy,...` rows). Columns are identified by the Units row
 * (3-letter code) or the `A$1=USD` / `AUD/USD` title/description; the TWI index column is skipped.
 * Base is AUD. Blank or non-numeric cells (e.g. CLOSED) are skipped.
 */
export const fromRbaCsv = (csv) => {
    const rows = parseCsv(csv, { delimiter: ',' });
    const meta = (label) => rows.find((r) => (r[0] ?? '').trim().toLowerCase() === label) ?? [];
    const units = meta('units');
    const title = meta('title');
    const desc = meta('description');
    const width = Math.max(...rows.map((r) => r.length));
    const quotes = [];
    for (let j = 1; j < width; j++) {
        const u = (units[j] ?? '').trim().toUpperCase();
        const m = /^[A-Z]{3}$/.exec(u) ?? /A\$1\s*=\s*([A-Z]{3})/.exec(title[j] ?? '') ?? /AUD\/([A-Z]{3})/.exec(desc[j] ?? '');
        quotes[j] = m ? (m[1] ?? m[0]) : null;
    }
    if (!quotes.some((q) => q))
        throw new KeelError('IMPORT_PARSE', 'No currency columns found in RBA CSV');
    const out = [];
    for (const r of rows) {
        const date = parseDate(r[0] ?? '', 'dd-MMM-yyyy');
        if (!date)
            continue;
        for (let j = 1; j < r.length; j++) {
            const q = quotes[j];
            const v = (r[j] ?? '').trim();
            const n = Number(v);
            if (q && v !== '' && Number.isFinite(n) && n > 0)
                out.push(mkRate('AUD', q, date, n, 'rba'));
        }
    }
    return out;
};
//# sourceMappingURL=rates.js.map